import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface ViaCEPResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ViaCEPService {
  // Usando uma API alternativa que não tem problemas de CORS
  private baseUrl = 'https://api.postmon.com.br/v1/cep';
  // Fallback para ViaCEP com JSONP
  private viaCepUrl = 'https://viacep.com.br/ws';
  private http = inject(HttpClient);


  /**
   * Busca endereço pelo CEP usando API alternativa
   * @param cep - CEP no formato 8 dígitos (ex: 01001000)
   * @returns Observable com os dados do endereço
   */
  buscarCEP(cep: string): Observable<ViaCEPResponse> {
    // Remove caracteres não numéricos
    const cleanCEP = cep.replace(/\D/g, '');

    // Valida se o CEP tem 8 dígitos
    if (cleanCEP.length !== 8) {
      return throwError(() => new Error('CEP deve conter 8 dígitos'));
    }

    // Primeiro tenta a API Postmon
    // return this.buscarComPostmon(cleanCEP).pipe(
      // catchError(() => {
        // Se falhar, tenta com ViaCEP via JSONP
        return this.buscarComViaCEP(cleanCEP);
      // })
    // );
  }

  /**
   * Busca usando API Postmon (sem problemas de CORS)
   */
  private buscarComPostmon(cep: string): Observable<ViaCEPResponse> {
    const url = `${this.baseUrl}/${cep}`;

    return this.http.get<any>(url).pipe(
      map(response => {
        if (response.erro) {
          throw new Error('CEP não encontrado');
        }

        return {
          cep: response.cep,
          logradouro: response.logradouro || '',
          complemento: response.complemento || '',
          bairro: response.bairro || '',
          localidade: response.cidade || response.localidade || '',
          uf: response.estado || response.uf || '',
          ibge: response.ibge || '',
          gia: response.gia || '',
          ddd: response.ddd || '',
          siafi: response.siafi || ''
        };
      }),
      catchError(error => {
        return throwError(() => new Error('Erro ao buscar CEP. Tente novamente.'));
      })
    );
  }

  /**
   * Busca usando ViaCEP com JSONP (fallback)
   */
  private buscarComViaCEP(cep: string): Observable<ViaCEPResponse> {
    return new Observable(observer => {
      // Criar script JSONP
      const script = document.createElement('script');
      const callbackName = 'viaCepCallback_' + crypto.randomUUID().replace(/-/g, '');

      // Definir callback global
      (window as any)[callbackName] = (data: any) => {
        // Limpar callback
        delete (window as any)[callbackName];
        document.head.removeChild(script);

        if (data.erro) {
          observer.error(new Error('CEP não encontrado'));
        } else {
          observer.next(data);
          observer.complete();
        }
      };

      // Configurar script
      script.src = `${this.viaCepUrl}/${cep}/json/?callback=${callbackName}`;
      script.onerror = () => {
        delete (window as any)[callbackName];
        document.head.removeChild(script);
        observer.error(new Error('Erro ao buscar CEP. Tente novamente.'));
      };

      // Adicionar script ao DOM
      document.head.appendChild(script);
    });
  }

  /**
   * Valida se um CEP está no formato correto
   * @param cep - CEP a ser validado
   * @returns true se o CEP é válido
   */
  validarCEP(cep: string): boolean {
    const cleanCEP = cep.replace(/\D/g, '');
    return cleanCEP.length === 8 && /^\d{8}$/.test(cleanCEP);
  }

  /**
   * Formata CEP para exibição (XXXXX-XXX)
   * @param cep - CEP a ser formatado
   * @returns CEP formatado
   */
  formatarCEP(cep: string): string {
    const cleanCEP = cep.replace(/\D/g, '');
    if (cleanCEP.length === 8) {
      return cleanCEP.replace(/^(\d{5})(\d{3})$/, '$1-$2');
    }
    return cep;
  }

  /**
   * Limpa CEP removendo caracteres especiais
   * @param cep - CEP a ser limpo
   * @returns CEP apenas com números
   */
  limparCEP(cep: string): string {
    return cep.replace(/\D/g, '');
  }

  /**
   * Busca CEP usando fetch nativo (alternativa para evitar CORS)
   */
  buscarCEPComFetch(cep: string): Promise<ViaCEPResponse> {
    const cleanCEP = cep.replace(/\D/g, '');

    if (cleanCEP.length !== 8) {
      return Promise.reject(new Error('CEP deve conter 8 dígitos'));
    }

    return fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Erro na requisição');
      }
      return response.json();
    })
    .then(data => {
      if (data.erro) {
        throw new Error('CEP não encontrado');
      }
      return data;
    });
  }

  /**
   * Fallback offline com CEPs comuns
   */
  buscarCEPOffline(cep: string): ViaCEPResponse | null {
    const cleanCEP = cep.replace(/\D/g, '');

    // Alguns CEPs comuns para fallback
    const cepsOffline: Record<string, ViaCEPResponse> = {
      '01001000': {
        cep: '01001-000',
        logradouro: 'Praça da Sé',
        complemento: 'lado ímpar',
        bairro: 'Sé',
        localidade: 'São Paulo',
        uf: 'SP',
        ibge: '3550308',
        gia: '1004',
        ddd: '11',
        siafi: '7107'
      },
      '20040020': {
        cep: '20040-020',
        logradouro: 'Rua do Ouvidor',
        complemento: '',
        bairro: 'Centro',
        localidade: 'Rio de Janeiro',
        uf: 'RJ',
        ibge: '3304557',
        gia: '',
        ddd: '21',
        siafi: '6001'
      },
      '22070011': {
        cep: '22070-011',
        logradouro: 'Av. Nossa Senhora de Copacabana',
        complemento: '',
        bairro: 'Copacabana',
        localidade: 'Rio de Janeiro',
        uf: 'RJ',
        ibge: '3304557',
        gia: '',
        ddd: '21',
        siafi: '6001'
      }
    };

    return cepsOffline[cleanCEP] || null;
  }
}
